import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@heroui/react';
import { LoadError } from '@/components/ui/LoadError';
import { Button } from '@/components/Button';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { formatDateTime } from '@/components/serverSettings/auditFormatting';
import type { BugReportItem, BugReportStatus } from '@bunker/shared';

/**
 * Beta bug-report queue — the platform-moderator view of user-submitted bug
 * reports (description + optional screenshot). Read + resolve are authorized
 * server-side; this trusts the host's access decision (platform moderators).
 */

const STATUS_FILTERS: (BugReportStatus | 'all')[] = ['open', 'resolved', 'dismissed', 'all'];

export function BugReportQueue() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [status, setStatus] = useState<BugReportStatus | 'all'>('open');

  const q = useInfiniteQuery({
    queryKey: ['bugReports', status] as const,
    queryFn: ({ pageParam }) =>
      api.listBugReports({
        ...(status !== 'all' ? { status } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['bugReports'] });

  const [pendingId, setPendingId] = useState<string | null>(null);
  const resolveM = useMutation({
    mutationFn: (vars: { id: string; status: 'resolved' | 'dismissed' }) =>
      api.resolveBugReport(vars.id, { status: vars.status }),
    onMutate: (vars) => setPendingId(vars.id),
    onSuccess: () => {
      invalidate();
      toast.success(t('mod.resolved'));
    },
    onError: (err) => toast.error(...errToToast(err, t('mod.resolveError'))),
    onSettled: () => setPendingId(null),
  });
  const resolveMutate = resolveM.mutate;
  const onResolve = useCallback(
    (id: string, s: 'resolved' | 'dismissed') => resolveMutate({ id, status: s }),
    [resolveMutate],
  );

  const reports = q.data?.pages.flatMap((p) => p.reports) ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t('mod.filterLabel')}>
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={status === s}
            onClick={() => setStatus(s)}
            className={
              'rounded-full px-3 py-1 text-xs transition-colors ' +
              (status === s
                ? 'font-semibold bg-accent/15 text-accent ring-1 ring-inset ring-accent/40'
                : 'text-ink-muted hover:bg-white/8 hover:text-ink')
            }
          >
            {t(`beta.filter.${s}`)}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner />
        </div>
      ) : q.isError ? (
        // A failed fetch used to render as "нет отчётов".
        <LoadError error={q.error} onRetry={() => void q.refetch()} />
      ) : reports.length === 0 ? (
        <p className="px-1 py-8 text-center text-xs text-ink-muted">{t('beta.queueEmpty')}</p>
      ) : (
        <>
          {reports.map((r) => (
            <BugCard key={r.id} report={r} busy={pendingId === r.id} onResolve={onResolve} />
          ))}
          {q.hasNextPage && (
            <div className="grid place-items-center pt-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={q.isFetchingNextPage}
                onClick={() => void q.fetchNextPage()}
              >
                {q.isFetchingNextPage ? t('mod.loadingMore') : t('mod.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const BugCard = memo(function BugCard(props: {
  report: BugReportItem;
  busy: boolean;
  onResolve: (id: string, status: 'resolved' | 'dismissed') => void;
}) {
  const { t } = useTranslation();
  const r = props.report;
  const open = r.status === 'open';

  return (
    <div
      className="space-y-2.5 rounded-xl p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={r.status} />
        {r.path && <span className="truncate font-mono text-[10px] text-ink-muted">{r.path}</span>}
        <span className="ml-auto text-[11px] tabular-nums text-ink-muted">{formatDateTime(r.createdAt)}</span>
      </div>

      <div className="whitespace-pre-wrap break-words text-xs text-ink-dim">{r.description}</div>

      {r.screenshotUrl && (
        <a href={r.screenshotUrl} target="_blank" rel="noopener noreferrer" className="block w-fit">
          <img
            src={r.screenshotUrl}
            alt={t('beta.screenshotAlt')}
            className="max-h-48 rounded-lg border border-separator object-contain transition-opacity hover:opacity-90"
          />
        </a>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-ink-muted">
          {t('mod.reportedBy', { name: r.reporter ? r.reporter.displayName || r.reporter.username : '—' })}
        </span>
        {open ? (
          <div className="ml-auto flex items-center gap-1.5">
            <Button variant="secondary" size="sm" disabled={props.busy} onClick={() => props.onResolve(r.id, 'dismissed')}>
              {t('beta.action.dismiss')}
            </Button>
            <Button variant="primary" size="sm" disabled={props.busy} onClick={() => props.onResolve(r.id, 'resolved')}>
              {t('beta.action.resolve')}
            </Button>
          </div>
        ) : (
          r.reviewer && (
            <span className="ml-auto text-[11px] text-ink-muted">
              {t('mod.resolvedBy', { name: r.reviewer.displayName || r.reviewer.username })}
            </span>
          )
        )}
      </div>
    </div>
  );
});

function StatusBadge(props: { status: BugReportStatus }) {
  const { t } = useTranslation();
  const tone =
    props.status === 'resolved'
      ? 'bg-success/15 !text-success-ink'
      : props.status === 'dismissed'
        ? 'bg-white/8 text-ink-muted'
        : 'bg-accent/12 text-accent';
  return (
    <span className={`font-medium rounded-full px-2 py-0.5 text-[10px] ${tone}`}>
      {t(`beta.status.${props.status}`)}
    </span>
  );
}
